import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useFormik } from 'formik';
import { Constants, Validators } from 'core';
import { CreateHelpInfoParams, HelpInfo, RequestError, ServiceType, Situations } from 'types';
import { Input as AntdInput } from 'antd';
import styled from 'styled-components';
import { path } from 'ramda';

import FormError from 'components/molecules/form-error';
import Input from 'components/organisms/input';
import { useIntl } from 'common/config/i18n';
import Button from 'components/molecules/button';
import { StyledRepositoryForm, StyledRepositoryFormButtonWrapper } from 'common/styling/mixins';
import InputSelect from 'components/organisms/select';

import { Editor } from '../react-quill';

const Label = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;
const { TextArea } = AntdInput;

type FormValues = Omit<CreateHelpInfoParams, 'longMessageBrut'>;

interface Props {
  onSubmit: (formValues: FormValues) => void;
  helpInfo?: HelpInfo;
  setLongMessageBrut?: Dispatch<SetStateAction<string>>;
  error?: RequestError;
  status?: 'loading' | 'error' | 'success';
  situationsCategory: Situations[];
  serviceTypes: ServiceType[];
}

const FormHelpInfo: React.FC<Props> = ({
  onSubmit,
  helpInfo,
  setLongMessageBrut,
  error,
  status,
  situationsCategory,
  serviceTypes,
}) => {
  const { t } = useIntl();
  const isUpdate = !!helpInfo;

  const initialValues = {
    longMessage: helpInfo?.longMessage || '',
    shortMessage: helpInfo?.shortMessage || undefined,
    subFamily: helpInfo?.subFamily || undefined,
    situationsIDs: helpInfo?.situations?.map((item) => item.id) || [],
    serviceTypeIDs: helpInfo?.serviceTypeIDs || (serviceTypes.length === 1 ? [serviceTypes[0].id] : []),
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.formHelpInfo(values),
    onSubmit: (values) => {
      onSubmit(values as FormValues);
    },
  });

  const selectSituationsData = useMemo(() => {
    if (situationsCategory) {
      return situationsCategory?.map((situationCategory, index) => ({
        id: `${index}-${situationCategory}`,
        label: situationCategory?.name,
        list: situationCategory?.situations || [],
      }));
    }
    return [];
  }, [situationsCategory]);

  const selectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        serviceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <StyledRepositoryForm onSubmit={formik.handleSubmit}>
      <div className="formSection">
        <Label>{t('HelpInfoPage', 'LONG_MSG')}</Label>
        <Editor
          value={formik.values.longMessage}
          onChange={({ content, textWithoutHtml }) => {
            if (setLongMessageBrut && content && textWithoutHtml) {
              if (textWithoutHtml?.trim().length === 0) {
                setLongMessageBrut('');
                formik.setFieldValue('longMessage', '');
              } else {
                setLongMessageBrut(textWithoutHtml);
                formik.setFieldValue('longMessage', content);
              }
            }
          }}
        />
        <FormError formik={formik} fieldName="longMessage" />
      </div>

      <div className="formSection">
        <Label>{t('HelpInfoPage', 'SHORT_MSG')}</Label>
        <TextArea
          rows={4}
          name="shortMessage"
          onChange={formik.handleChange}
          defaultValue={formik.values.shortMessage}
        />
        <FormError formik={formik} fieldName="shortMessage" />
      </div>

      <div className="formSection">
        <Label>{t('HelpInfoPage', 'SITUATION_CHOICE')}</Label>
        <InputSelect
          mode="multiple"
          onChange={(situationsIDs) => formik.setFieldValue('situationsIDs', situationsIDs)}
          defaultValue={formik.values.situationsIDs}
          data={selectSituationsData}
        />
        <FormError formik={formik} fieldName="situationsIDs" />
      </div>

      <div className="formSection">
        <Label>{t('HelpInfoPage', 'CIRCULATION_TYPES_CHOICE')}</Label>
        <InputSelect
          mode="multiple"
          onChange={(serviceTypeIDs) => formik.setFieldValue('serviceTypeIDs', serviceTypeIDs)}
          defaultValue={formik.values.serviceTypeIDs}
          data={selectServiceTypesData}
        />
        <FormError formik={formik} fieldName="serviceTypeIDs" />
      </div>

      <div className="formSection">
        <Input
          upLabel={t('HelpInfoPage', 'SUB_FAMILY')}
          name="subFamily"
          onChange={formik.handleChange}
          value={formik.values.subFamily}
          width="100%"
        />
        <FormError formik={formik} fieldName="SubFamily" />
      </div>

      <StyledRepositoryFormButtonWrapper>
        <Button
          height="35px"
          width="100%"
          borderRadius="20px"
          disabled={
            !!(formik.errors.longMessage && formik.touched.longMessage) ||
            !!(formik.errors.shortMessage && formik.touched.shortMessage) ||
            !!(formik.errors.subFamily && formik.touched.subFamily) ||
            !!(formik.errors.situationsIDs && formik.touched.situationsIDs)
          }
          htmlType="submit"
          loading={status === 'loading'}
        >
          {t('Buttons', isUpdate ? 'SAVE_BUTTON' : 'CREATE_BUTTON')}
        </Button>
      </StyledRepositoryFormButtonWrapper>
      {error && (
        <FormError
          message={
            error === Constants.ALREADY_EXISTED_PROBLEM_CODE
              ? t('HelpInfoPage', 'ALLREADY_EXIST')
              : t('form', 'FORM_UNKNOWN_ERROR')
          }
        />
      )}
    </StyledRepositoryForm>
  );
};

export default FormHelpInfo;
